/**
 * useInViewOnce — Detecta la primera vez que un elemento entra al viewport.
 *
 * QUÉ HACE: entrega un `ref` para el nodo y un booleano `inView` que pasa a
 * `true` una sola vez (el observer se desconecta al primer cruce). Se usa para
 * disparar la animación de entrada solo de lo que el usuario ve.
 * QUÉ INVOCA: IntersectionObserver (con fallback a `true` si no existe).
 * QUÉ EXPORTA: useInViewOnce.
 * QUÉ NO DEBE HACER: no conoce datos, dominio ni estilos.
 */
import { useEffect, useRef, useState } from "react";
import type { RefObject } from "react";

export interface UseInViewOnceOptions {
  /** Margen del viewport para anticipar la entrada (por defecto `0px`). */
  rootMargin?: string;
  /** Umbral de visibilidad requerido (por defecto `0.15`). */
  threshold?: number;
  /** Si es `false`, no observa y queda en `false` (para desactivar la entrada). */
  enabled?: boolean;
}

export interface UseInViewOnceResult<T extends Element> {
  ref: RefObject<T>;
  inView: boolean;
}

export function useInViewOnce<T extends Element = HTMLDivElement>(
  options: UseInViewOnceOptions = {},
): UseInViewOnceResult<T> {
  const { rootMargin = "0px", threshold = 0.15, enabled = true } = options;
  const ref = useRef<T>(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    if (!enabled || inView) return;
    const node = ref.current;
    if (!node) return;
    if (typeof IntersectionObserver === "undefined") {
      setInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { rootMargin, threshold },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [enabled, inView, rootMargin, threshold]);

  return { ref, inView };
}
